/**
 * Relative-date humanizer for list rows:
 * Tomorrow / In N days / Today / Yesterday / N days ago / N weeks ago / absolute date.
 *
 * Consolidates five hand-rolled copies (Triggers, Team, Dashboards, Workflows,
 * UnifiedDashboard). Fixes two bugs those copies had:
 *   - future dates rendered as past (some used Math.abs on the diff), and
 *   - missing null/invalid-date guards.
 */
export interface RelativeDateOptions {
  /** Returned for empty or unparseable input. Default '—'. */
  fallback?: string;
  /** 'long' → "3 days ago" / "2 weeks ago" (default); 'short' → "3d ago" / "2w ago". */
  style?: 'long' | 'short';
  /** When set, the absolute (>30d / far-future) fallback renders en-US with these options. */
  localeFormat?: Intl.DateTimeFormatOptions;
}

export function formatRelativeDate(iso?: string | null, options: RelativeDateOptions = {}): string {
  const { fallback = '—', style = 'long', localeFormat } = options;
  if (!iso) return fallback;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return fallback;

  const absolute = () =>
    localeFormat ? date.toLocaleDateString('en-US', localeFormat) : date.toLocaleDateString();

  // Signed: negative diff = future (the copies that used Math.abs got this wrong).
  const diffDays = Math.floor((Date.now() - date.getTime()) / 86_400_000);

  if (diffDays < 0) {
    const ahead = -diffDays;
    if (ahead === 1) return 'Tomorrow';
    if (ahead < 7) return `In ${ahead} days`;
    return absolute();
  }
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return style === 'short' ? `${diffDays}d ago` : `${diffDays} days ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return style === 'short' ? `${weeks}w ago` : `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  }
  return absolute();
}
